import {
  Box,
  Button,
  ClickAwayListener,
  Popper,
  Stack,
  Typography,
  styled,
} from "@mui/material";
import React, { FC, useEffect, useState } from "react";
import { DateRangePicker, DateRangePickerProps } from "react-date-range";
import "react-date-range/dist/styles.css"; // main style file
import "react-date-range/dist/theme/default.css";
import { getDateFormatted } from "src/shared/components/Table/ui/TableFilters/TableFilterCalendar/helpers/dateFormat.helpers";
import { getStaticRanges } from "../model/constants";
import { getCalendarState } from "../model/helpers";

const StyledDateRangePicker = styled(DateRangePicker)(({ theme }) => ({
  ["span"]: theme.typography,
  [".rdrStaticRangeSelected, .rdrDayHovered, .rdrInRange, .rdrStartEdge, .rdrEndEdge"]:
    {
      color: `${theme.palette.primary.main}!important`,
    },
  [".rdrDateDisplayWrapper, .rdrNextPrevButton, .rdrStaticRange:hover .rdrStaticRangeLabel"]:
    {
      backgroundColor: "rgba(168, 169, 75, 0.1) !important",
    },
  ["rdrNextPrevButton:hover"]: {
    backgroundColor: "rgba(168, 169, 75, 0.25) !important",
  },
  [".rdrDateDisplayItemActive, .rdrDayStartPreview, .rdrDayInPreview, .rdrDayEndPreview"]:
    {
      borderColor: `${theme.palette.primary.main}!important`,
    },
  [".rdrDayToday .rdrDayNumber span:after"]: {
    backgroundColor: `${theme.palette.primary.main}!important`,
  },
  [".rdrCalendarWrapper.rdrDateRangeWrapper"]: {
    padding: theme.spacing(1, 7.5, 6, 6),
  },
  [".rdrDefinedRangesWrapper"]: {
    borderColor: theme.palette.grey[300],
    paddingTop: theme.spacing(5),
  },

  [theme.breakpoints.down("md")]: {
    flexWrap: "wrap",

    [".rdrDefinedRangesWrapper"]: { width: "100%" },
    [".rdrStaticRanges"]: {
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      flexDirection: "row",
    },
    [".rdrMonths.rdrMonthsHorizontal"]: { display: "flex", flexWrap: "wrap" },
  },
}));

interface ICalendarProps extends Omit<DateRangePickerProps, "onChange"> {
  onChange: (v: ICalendarValue) => void;
  value: ICalendarValue;
  anchor: HTMLElement | null;
  onClose: () => void;
}

interface ICalendarState {
  startDate: Date;
  endDate: Date;
  key: "selection";
}

interface ICalendarValue {
  startDate: Date;
  endDate: Date;
}

export const RangeCalendar: FC<ICalendarProps> = ({
  value,
  anchor,
  onChange,
  onClose,
  ...dateRangeProps
}) => {
  const [state, setState] = useState<ICalendarState>(() => ({
    key: "selection",
    ...getCalendarState(value),
  }));

  const handleApply = () => {
    onChange({
      startDate: state.startDate,
      endDate: state.endDate,
    });
    onClose();
  };

  useEffect(() => {
    // if value has been changed outside
    setState({
      key: "selection",
      ...getCalendarState(value),
    });
  }, [value]);

  return (
    <Popper
      open={!!anchor}
      anchorEl={anchor}
      disablePortal={false}
      sx={{
        zIndex: 100,
      }}
      modifiers={[
        {
          name: "preventOverflow",
          enabled: true,
          options: {
            altAxis: true,
            altBoundary: true,
            tether: true,
            rootBoundary: "document",
            padding: 8,
          },
        },
      ]}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Box sx={{ pt: 2 }}>
          <Box
            sx={(theme) => ({
              boxShadow: theme.shadows[13],
              borderRadius: 4.5,
              backgroundColor: theme.palette.common.white,
            })}
          >
            <StyledDateRangePicker
              {...dateRangeProps}
              ranges={[state]}
              onChange={({ selection }) => {
                setState(selection as ICalendarState);
              }}
              staticRanges={getStaticRanges()}
              inputRanges={[]}
              months={2}
              direction="horizontal"
              showDateDisplay={false}
              weekStartsOn={1}
            />
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                py: 5,
                px: 7.5,
                borderTop: "1px solid",
                borderColor: "grey.300",
              }}
            >
              <Box>
                <Typography>
                  {getDateFormatted(state.startDate, state.endDate)}
                </Typography>
              </Box>
              <Stack spacing={4} direction="row">
                <Button
                  variant="outlined"
                  onClick={onClose}
                  sx={{
                    borderRadius: (theme) => theme.spacing(4.5),
                  }}
                >
                  {"Отмена"}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleApply}
                  sx={{
                    borderRadius: (theme) => theme.spacing(4.5),
                  }}
                >
                  Применить
                </Button>
              </Stack>
            </Box>
          </Box>
        </Box>
      </ClickAwayListener>
    </Popper>
  );
};
